'use client'

import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useRef, useState } from 'react'
import { useRouteParam, useUpdateRouteParam } from '@/hooks/use-route-params'
import { RouteParamForm } from '@/components/partials/route-params'
import { type RouteParamFormRef } from '@/components/partials/route-params/form'
import { FormSkeleton, ContentError } from '@/components/partials'
import { UpdateRouteParamParams } from '@/types/route-param'
import { FormModeEnum } from '@/enums'

interface UpdateProps {
  id: string
}

const Update = ({ id }: UpdateProps) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const messageFormRef = useRef<RouteParamFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: message, isLoading, error } = useRouteParam(id)
  const { mutate: updateRouteParam, isPending } = useUpdateRouteParam()

  const handleSubmit = (data: UpdateRouteParamParams) => {
    updateRouteParam(
      { messageId: id, params: data },
      {
        onSuccess: async updatedMessage => {
          setIsRedirecting(true)
          queryClient.setQueryData(['routeParam', id], updatedMessage)
          await queryClient.invalidateQueries({ queryKey: ['routeParams'] })
          toast.success(t('updated_successfully'))
          router.push(`/localization/route-params/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (messageFormRef.current) {
        messageFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('updating_message'))
  }

  const handleCancel = () => {
    router.push(`/localization/route-params/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} />
  }

  if (error || !message) {
    return <ContentError error={error} listUrl="/localization/route-params" />
  }

  return (
    <RouteParamForm
      ref={messageFormRef}
      mode={FormModeEnum.UPDATE}
      message={message}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
